// ─────────────────────────────────────────────────────────
// Einstellungen → Rollen & Zugriffsrechte
// ─────────────────────────────────────────────────────────
// Eigenes Modul im Einstellungen-Stil (löst die alte OrgPanel-Rollen-UI ab).
// Nutzt /api/roles (org-scoped, roles:read/write).
// Enthält den VOLLSTÄNDIGEN Berechtigungs-Katalog inkl. der neuen Bereiche
// (Zeiterfassung, Arbeitszeitmodelle, Lohngruppen, Feiertage, Prompts, KI-Zugänge).
// Sprache: alle sichtbaren Texte über t() — Keys in i18n/de.json + en.json.
import { icon, escapeHtml } from '../../utils/dom.js';
import { getUser } from '../../core/auth.js';
import { createRole, updateRole, deleteRole } from '../../core/org.js';
import { t } from '../../i18n/index.js';

// Berechtigungs-Gruppen — NUR Codes, keine Labels.
// Der Berechtigungs-CODE ist zugleich der Übersetzungs-Key (settings.roles.perms.<code>),
// die Gruppe wird über ihren key übersetzt (settings.roles.groups.<key>).
// Bewusst KEINE Labels in dieser Konstante: sie entsteht beim Modul-Laden und würde die
// Beschriftungen für immer in der Startsprache einfrieren (siehe docs/i18n.md, Regel 3).
// Deckt alle ORG_PERMISSIONS ab (Quelle: backend/src/config/permissions.ts) — Stand
// 2026-08-01 ergänzt: Gutscheine (vouchers:read/write) — 47 Rechte.
const PERM_GROUPS = [
  { key: 'customers', perms: ['customers:read', 'customers:create', 'customers:delete', 'team:activity:read'] },
  { key: 'documents', perms: ['documents:read', 'documents:create', 'documents:send'] },
  { key: 'vouchers', perms: ['vouchers:read', 'vouchers:write'] },
  { key: 'time', perms: ['time:read', 'time:write', 'time:approve'] },
  { key: 'worktime', perms: ['worktime:read', 'worktime:write'] },
  { key: 'wages', perms: ['wages:read', 'wages:write'] },
  { key: 'calculation', perms: ['units:read', 'units:write', 'surcharges:read', 'surcharges:write', 'bausteine:read', 'bausteine:write'] },
  { key: 'accounting', perms: ['accounting:read', 'accounting:write'] },
  { key: 'layout', perms: ['layout:read', 'layout:write'] },
  { key: 'holidays', perms: ['holidays:read', 'holidays:write'] },
  { key: 'leads', perms: ['leads:read', 'leads:write'] },
  { key: 'appointments', perms: ['appointments:read', 'appointments:write'] },
  { key: 'members', perms: ['members:read', 'members:invite', 'members:write', 'members:remove'] },
  { key: 'roles', perms: ['roles:read', 'roles:write'] },
  { key: 'org', perms: ['org:settings'] },
  { key: 'communication', perms: ['email:read', 'whatsapp:send'] },
  { key: 'ai', perms: ['prompts:read', 'prompts:write', 'credentials:read', 'credentials:write'] },
  { key: 'automation', perms: ['scenarios:read', 'scenarios:write', 'scenarios:delete', 'scenarios:run', 'connections:read', 'connections:write', 'connections:delete', 'executions:read'] },
];
const PLATFORM_GROUP = { key: 'platform', perms: ['platform:orgs:read', 'platform:orgs:write', 'platform:audit:read'] };

// Der Code enthält kein '.', darum taugt er direkt als Key-Segment.
const groupTitle = (key) => t('settings.roles.groups.' + key);
const permLabel = (code) => t('settings.roles.perms.' + code);

function isPlatformOrg() {
  return getUser()?.organization?.isPlatform === true;
}
function groups() {
  return isPlatformOrg() ? [...PERM_GROUPS, PLATFORM_GROUP] : PERM_GROUPS;
}

// ── Listen-Bereich ────────────────────────────────────────
export function renderRolesSection(ctx) {
  const { s, can } = ctx;
  if (!can('roles:read')) return `<div class="cd-set-empty">${escapeHtml(t('common.noPermission'))}</div>`;
  if (s.loading) return `<div class="cd-set-empty">${escapeHtml(t('common.loading'))}</div>`;

  const roles = s.roles || [];
  const info = `
    <div class="cd-emp-info">${icon('info', 18)}<span>${escapeHtml(t('settings.roles.info'))}</span></div>`;

  const rows = roles.length
    ? roles
        .map((r) => {
          const clickable = can('roles:write') && !r.isSystem;
          const clickAttr = clickable ? ` data-action="rl-edit-open" data-id="${escapeHtml(r.id)}"` : '';
          const badge = r.isSystem ? ` <span class="cd-wtm-badge">${escapeHtml(t('settings.roles.systemBadge'))}</span>` : '';
          const permCount = r.permissions ? r.permissions.length : 0;
          const userCount = r._count ? r._count.users : 0;
          return `
            <div class="cd-rl-trow${clickable ? ' cd-emp-trow-click' : ''}"${clickAttr}>
              <div class="cd-rl-name">${icon('vpn_key', 18)}<div><span>${escapeHtml(r.name)}${badge}</span>${r.description ? `<small>${escapeHtml(r.description)}</small>` : ''}</div></div>
              <div class="cd-rl-num">${escapeHtml(t('settings.roles.permCount', { count: permCount }))}</div>
              <div class="cd-rl-num">${escapeHtml(t('settings.roles.userCount', { count: userCount }))}</div>
            </div>`;
        })
        .join('')
    : `<div class="cd-emp-empty">${escapeHtml(t('settings.roles.empty'))}</div>`;

  return `
    <div class="cd-emp">
      ${info}
      <div class="cd-emp-card">
        <div class="cd-emp-card-head">${icon('vpn_key', 18)}<h3>${escapeHtml(t('settings.roles.title'))}</h3></div>
        <div class="cd-emp-table">${rows}</div>
      </div>
    </div>`;
}

export function rolesHeadButton(ctx) {
  if (!ctx.can('roles:write')) return '';
  return `<button class="cd-btn-primary" data-action="rl-create-open">${icon('add', 16)}${escapeHtml(t('settings.roles.create'))}</button>`;
}

// ── Modal (Erstellen / Bearbeiten) ────────────────────────
export function renderRoleModal(ctx) {
  const { s } = ctx;
  const e = s.roleEdit;
  if (!e || !e.open) return '';
  const dis = e.saving ? 'disabled' : '';
  const isEdit = !!e.id;
  const current = new Set(e.permissions || []);

  const groupsHtml = groups()
    .map(
      (g) => `
      <div class="cd-rl-group">
        <div class="cd-rl-group-title">${escapeHtml(groupTitle(g.key))}</div>
        <div class="cd-rl-perms">
          ${g.perms
            .map(
              (p) => `
            <label class="cd-rl-check">
              <input type="checkbox" data-rl-perm value="${escapeHtml(p)}" ${current.has(p) ? 'checked' : ''} ${dis}>
              <span>${escapeHtml(permLabel(p))}</span>
            </label>`,
            )
            .join('')}
        </div>
      </div>`,
    )
    .join('');

  const footBtns = isEdit
    ? (e.confirmDelete
        ? `<div class="cd-wtm-confirm"><span>${escapeHtml(t('settings.roles.confirmDeleteQuestion'))}</span>
             <button class="cd-btn-ghost" data-action="rl-delete-cancel" ${dis}>${escapeHtml(t('common.cancel'))}</button>
             <button class="cd-btn-danger" data-action="rl-delete-confirm" ${dis}>${escapeHtml(e.saving ? t('common.deleting') : t('common.confirmDelete'))}</button></div>`
        : `<button class="cd-btn-danger-ghost" data-action="rl-delete" ${dis}>${icon('delete', 16)}${escapeHtml(t('settings.roles.delete'))}</button>
           <div class="cd-wtm-foot-spacer"></div>
           <button class="cd-btn-ghost" data-action="rl-close" ${dis}>${escapeHtml(t('common.cancel'))}</button>
           <button class="cd-btn-primary" data-action="rl-submit" ${dis}>${escapeHtml(e.saving ? t('common.saving') : t('common.save'))}</button>`)
    : `<div class="cd-wtm-foot-spacer"></div>
       <button class="cd-btn-ghost" data-action="rl-close" ${dis}>${escapeHtml(t('common.cancel'))}</button>
       <button class="cd-btn-primary" data-action="rl-submit" ${dis}>${escapeHtml(e.saving ? t('common.saving') : t('common.save'))}</button>`;

  return `
    <div class="cd-emp-modal-overlay" data-action="rl-close">
      <div class="cd-emp-modal cd-rl-modal" data-action="rl-noop">
        <div class="cd-emp-modal-head">
          <h2>${icon('vpn_key', 20)}${escapeHtml(isEdit ? t('settings.roles.editTitle') : t('settings.roles.create'))}</h2>
          <button class="cd-emp-modal-x" data-action="rl-close" title="${escapeHtml(t('common.close'))}">${icon('close', 20)}</button>
        </div>
        <div class="cd-emp-modal-body">
          ${e.error ? `<div class="cd-error">${icon('error', 18)}<span>${escapeHtml(e.error)}</span></div>` : ''}
          <div class="cd-form-grid2">
            <label class="cd-field"><span>${escapeHtml(t('settings.roles.nameLabel'))}</span><input class="cd-input" id="rl-name" type="text" placeholder="${escapeHtml(t('settings.roles.namePlaceholder'))}" value="${escapeHtml(e.name || '')}" ${dis}></label>
            <label class="cd-field"><span>${escapeHtml(t('settings.roles.description'))}</span><input class="cd-input" id="rl-desc" type="text" value="${escapeHtml(e.description || '')}" ${dis}></label>
          </div>
          <div class="cd-form-section">${escapeHtml(t('settings.roles.permissionsSection'))}</div>
          <div class="cd-rl-groups">${groupsHtml}</div>
        </div>
        <div class="cd-emp-modal-foot">${footBtns}</div>
      </div>
    </div>`;
}

// ── Aktions-Handler ───────────────────────────────────────
export function handleRoleAction(a, el, ctx) {
  const { s, draw, reload } = ctx;
  switch (a) {
    case 'rl-noop':
      return true;
    case 'rl-create-open':
      if (!ctx.can('roles:write')) return true;
      s.roleEdit = { open: true, id: null, name: '', description: '', permissions: [], saving: false, error: '', confirmDelete: false };
      draw();
      return true;
    case 'rl-edit-open': {
      if (!ctx.can('roles:write')) return true;
      const r = (s.roles || []).find((x) => x.id === el.dataset.id);
      if (!r || r.isSystem) return true;
      s.roleEdit = {
        open: true, id: r.id, name: r.name, description: r.description || '',
        permissions: [...(r.permissions || [])], saving: false, error: '', confirmDelete: false,
      };
      draw();
      return true;
    }
    case 'rl-close':
      s.roleEdit = null;
      draw();
      return true;
    case 'rl-submit': {
      const e = s.roleEdit;
      if (!e) return true;
      const name = (document.getElementById('rl-name')?.value || '').trim();
      if (!name) { e.error = t('settings.roles.nameRequired'); draw(); return true; }
      const permissions = Array.from(document.querySelectorAll('[data-rl-perm]:checked')).map((x) => x.value);
      const description = (document.getElementById('rl-desc')?.value || '').trim();
      e.saving = true; e.error = ''; draw();
      const payload = { name, description, permissions };
      const req = e.id ? updateRole(e.id, payload) : createRole(payload);
      req
        .then(() => { s.roleEdit = null; if (reload) reload(); else draw(); })
        .catch((err) => { if (s.roleEdit) { s.roleEdit.saving = false; s.roleEdit.error = err.message || t('common.saveFailed'); } draw(); });
      return true;
    }
    case 'rl-delete': {
      const e = s.roleEdit;
      if (!e) return true;
      e.confirmDelete = true; draw();
      return true;
    }
    case 'rl-delete-cancel': {
      const e = s.roleEdit;
      if (!e) return true;
      e.confirmDelete = false; draw();
      return true;
    }
    case 'rl-delete-confirm': {
      const e = s.roleEdit;
      if (!e || !e.id) return true;
      e.saving = true; e.error = ''; draw();
      deleteRole(e.id)
        .then(() => { s.roleEdit = null; if (reload) reload(); else draw(); })
        .catch((err) => { if (s.roleEdit) { s.roleEdit.saving = false; s.roleEdit.confirmDelete = false; s.roleEdit.error = err.message || t('common.deleteFailed'); } draw(); });
      return true;
    }
    default:
      return false;
  }
}
